import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Tag, Plus, Trash2, Loader2, Copy, CheckCircle2 } from "lucide-react";

const empty = {
  code: "", discount_pct: "", discount_amount: "",
  expires_at: "", max_uses: "", min_purchase: "", description: "",
};

const PromoCodes = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [copied, setCopied] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/promo-codes");
      setList(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load promo codes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        currency: "GBP",
        discount_pct: form.discount_pct ? parseFloat(form.discount_pct) : null,
        discount_amount: form.discount_amount ? parseFloat(form.discount_amount) : null,
        expires_at: form.expires_at || null,
        max_uses: form.max_uses ? parseInt(form.max_uses, 10) : null,
        min_purchase: form.min_purchase ? parseFloat(form.min_purchase) : null,
        description: form.description || null,
      };
      if (!payload.discount_pct && !payload.discount_amount) {
        toast.error("Set either a discount % or a flat £ amount");
        setSaving(false);
        return;
      }
      await api.post("/admin/promo-codes", payload);
      toast.success(`Code ${payload.code} created 🎉`);
      setForm(empty);
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create code");
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (code) => {
    if (!window.confirm(`Deactivate ${code}? Existing uses are preserved, but no new redemptions.`)) return;
    try {
      await api.delete(`/admin/promo-codes/${code}`);
      toast.success("Deactivated");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const copy = (code) => {
    navigator.clipboard.writeText(code);
    setCopied(code);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <Card className="p-6 bg-white border-black/10" data-testid="promo-codes-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#4A5D23] text-white flex items-center justify-center">
            <Tag className="w-5 h-5" />
          </div>
          <div>
            <div className="label-overline text-[#C05A3C]">Marketing</div>
            <h3 className="font-display text-2xl leading-tight">Promo codes</h3>
            <div className="text-xs text-muted-foreground">
              Drive conversions with launch discounts, referral rewards, or seasonal sales
            </div>
          </div>
        </div>
        <Button
          onClick={() => setShowForm((v) => !v)}
          className="bg-[#1A2024] text-white hover:bg-[#1A2024]/90"
          data-testid="promo-add-btn"
        >
          <Plus className="w-4 h-4 mr-1" /> {showForm ? "Cancel" : "New code"}
        </Button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-[#F3EFE9] p-4 rounded-md mb-5 space-y-3" data-testid="promo-form">
          <div className="grid md:grid-cols-3 gap-3">
            <Field label="Code (e.g. LAUNCH15)" required>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="font-mono uppercase" placeholder="LAUNCH15" required data-testid="promo-code-input-admin" />
            </Field>
            <Field label="Discount %">
              <Input type="number" min="1" max="90" step="1" value={form.discount_pct} onChange={(e) => setForm({ ...form, discount_pct: e.target.value })} placeholder="e.g. 15" data-testid="promo-pct" />
            </Field>
            <Field label="OR flat £ off">
              <Input type="number" min="0.5" step="0.5" value={form.discount_amount} onChange={(e) => setForm({ ...form, discount_amount: e.target.value })} placeholder="e.g. 5.00" data-testid="promo-amount" />
            </Field>
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            <Field label="Expiry (optional)">
              <Input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} data-testid="promo-expiry" />
            </Field>
            <Field label="Max uses (optional)">
              <Input type="number" min="1" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} placeholder="Unlimited if blank" data-testid="promo-max-uses" />
            </Field>
            <Field label="Min purchase £ (optional)">
              <Input type="number" min="0" step="0.5" value={form.min_purchase} onChange={(e) => setForm({ ...form, min_purchase: e.target.value })} placeholder="0.00" data-testid="promo-min" />
            </Field>
          </div>
          <Field label="Description (admin notes — buyer never sees this)">
            <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g. 'Instagram launch campaign Feb 2026'" data-testid="promo-desc" />
          </Field>
          <Button type="submit" disabled={saving} className="bg-[#C05A3C] text-white hover:bg-[#C05A3C]/90" data-testid="promo-submit">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
            Create code
          </Button>
        </form>
      )}

      {loading ? (
        <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : list.length === 0 ? (
        <div className="text-sm text-muted-foreground italic" data-testid="promo-empty">
          No promo codes yet. Click &quot;New code&quot; to create your first one (e.g. LAUNCH15 for 15% off).
        </div>
      ) : (
        <div className="space-y-2" data-testid="promo-list">
          {list.map((c) => {
            const usedOut = c.max_uses && c.used_count >= c.max_uses;
            return (
              <div key={c.code} className={`flex items-center gap-3 p-3 border rounded-md bg-white ${c.is_active ? "border-black/10" : "border-black/5 opacity-60"}`} data-testid={`promo-row-${c.code}`}>
                <button onClick={() => copy(c.code)} className="font-mono text-lg font-semibold tracking-wider text-[#1A2024] hover:text-[#C05A3C] flex items-center gap-2 group" title="Copy">
                  {c.code}
                  {copied === c.code ? <CheckCircle2 className="w-4 h-4 text-[#4A5D23]" /> : <Copy className="w-3.5 h-3.5 opacity-0 group-hover:opacity-60" />}
                </button>
                <div className="flex-1 text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-1">
                  <span><strong className="text-[#1A2024]">{c.discount_pct ? `${c.discount_pct}% off` : `£${c.discount_amount} off`}</strong></span>
                  <span>Used <strong>{c.used_count || 0}{c.max_uses ? `/${c.max_uses}` : ""}</strong></span>
                  {c.expires_at && <span>Exp: {c.expires_at.slice(0, 10)}</span>}
                  {c.min_purchase && <span>Min: £{c.min_purchase}</span>}
                  {c.description && <span className="italic">{c.description}</span>}
                </div>
                {usedOut && <Badge className="bg-red-100 text-red-700 text-[10px]">Used out</Badge>}
                {!c.is_active && <Badge className="bg-gray-200 text-gray-700 text-[10px]">Inactive</Badge>}
                {c.is_active && (
                  <Button variant="ghost" size="sm" onClick={() => deactivate(c.code)} data-testid={`promo-deactivate-${c.code}`}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="text-xs font-medium text-[#1A2024] mb-1 block">{label}{required && <span className="text-[#C05A3C]"> *</span>}</span>
    {children}
  </label>
);

export default PromoCodes;
